/**
 * /dashboard/surveys/$id/results — HR-admin aggregate view (G16).
 *
 * Backend: GET /api/v1/surveys/{id}/aggregate. Anonymity-safe — never
 * returns per-user data when the parent survey is is_anonymous=true
 * (the SQL aggregate path doesn't GROUP BY user_id).
 *
 * Per the task brief, we do not surface an "Export CSV" button — the
 * backend doesn't expose a CSV endpoint for aggregates in v1.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Settings2 } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useAggregate } from "@/hooks/use-surveys";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { PageHeader } from "@/components/shared/page-header";
import { SurveyResultsView } from "@/components/surveys/survey-results-view";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/surveys/$id/results",
)({
  component: SurveyResultsRoute,
});

const SK_RES_KEYS = ["sk-r1", "sk-r2", "sk-r3"] as const;

function SurveyResultsRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const surveyId = Number(id);
  const q = useAggregate(surveyId);
  const { user } = useAuth();
  const isHR = isHRAdmin(user?.role);

  return (
    <>
      <PageHeader
        title={q.data?.survey.title ?? "Survey results"}
        description={
          q.data
            ? `${q.data.response_count.toLocaleString()} response${q.data.response_count === 1 ? "" : "s"}`
            : "Aggregate counts and averages — anonymity-safe."
        }
        actions={
          <div className="flex gap-2">
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/surveys" params={{ tenantSlug }}>
                <ArrowLeft aria-hidden className="h-4 w-4" />
                Back
              </Link>
            </Button>
            <Button asChild variant="outline" className="gap-1.5">
              <Link
                to="/app/$tenantSlug/surveys/$id"
                params={{tenantSlug, id: String(surveyId)}}
              >
                <Settings2 aria-hidden className="h-4 w-4" />
                Configure
              </Link>
            </Button>
          </div>
        }
      />

      {!isHR && (
        <Alert variant="destructive">
          <AlertTitle>HR-admin only</AlertTitle>
          <AlertDescription>
            Only HR managers and company admins can view survey results.
          </AlertDescription>
        </Alert>
      )}

      {isHR && q.isLoading && (
        <div className="space-y-4">
          {SK_RES_KEYS.map((k) => (
            <Card key={k}>
              <CardHeader>
                <Skeleton className="h-5 w-1/3" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isHR && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load results</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {isHR && q.data && <SurveyResultsView aggregate={q.data} />}
    </>
  );
}
